import React from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "../../lib/withRouter";
import Header2 from "../Header2";
import { fetchLeadsList, getLeadsListUrl, leadsListQueryFromSearchInput } from "../../lib/leadsListApi";
import { LeadScoreBadge } from "./leadScoreBadge";
import { LeadLoadingSpinner } from "./leadLoadingSpinner";
import { DobWithAgeDisplay, formatDobWithAge } from "./leadSummaryDynamic";
import { toTitleCaseLabel } from "./leadSummaryLabels";
import { getLeadStatusDisplay } from "./leadStatusDisplay";
import "./leadSummaryCritical.css";
import "./LeadsSummary.less";

const LEADS_PAGE_SIZE_OPTIONS = [50, 75, 100];
const DEFAULT_LEADS_PAGE_SIZE = 50;

/** Build compact 0-based page indexes with ellipsis gaps (`null`). */
function getVisiblePageIndexes(pageIndex, totalPages) {
    const total = Math.max(0, Number(totalPages) || 0);
    const current = Math.max(0, Math.min(total - 1, Number(pageIndex) || 0));
    if (total <= 9) {
        return Array.from({ length: total }, (_, i) => i);
    }

    const pages = new Set([0, total - 1]);
    for (let i = current - 2; i <= current + 2; i += 1) {
        if (i >= 0 && i < total) {
            pages.add(i);
        }
    }
    // Prefer a fuller start window when near the beginning (matches mock: 1–8 … last).
    if (current <= 4) {
        for (let i = 0; i < Math.min(8, total); i += 1) {
            pages.add(i);
        }
    }
    if (current >= total - 5) {
        for (let i = Math.max(0, total - 8); i < total; i += 1) {
            pages.add(i);
        }
    }

    const sorted = Array.from(pages).sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i += 1) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
            result.push(null);
        }
        result.push(sorted[i]);
    }
    return result;
}

function formatLeadsRecordRange(pageIndex, pageSize, totalCount) {
    const total = Math.max(0, Number(totalCount) || 0);
    const size = Math.max(1, Number(pageSize) || 1);
    const page = Math.max(0, Number(pageIndex) || 0);
    if (total === 0) {
        return "Showing records 0-0 of 0";
    }
    const start = page * size + 1;
    const end = Math.min((page + 1) * size, total);
    return `Showing records ${start.toLocaleString()}-${end.toLocaleString()} of ${total.toLocaleString()}`;
}

function formatLeadFullName(row) {
    const first = String(row?.first_name || "").trim();
    const last = String(row?.last_name || "").trim();
    const combined = [first, last].filter(Boolean).join(" ");
    const raw = (combined || String(row?.name || "").trim()).replace(/\s+/g, " ").trim();
    if (!raw) {
        return "—";
    }
    return raw.toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function leadRowKey(row, index) {
    const cin = String(row?.cin || "").trim();
    const fid = String(row?.fhir_id || "").trim();
    if (cin && fid) {
        return `${cin}__${fid}`;
    }
    return cin || fid || `idx-${index}`;
}

const SUMMARY_UNAVAILABLE_TOOLTIP = toTitleCaseLabel("Summary not generated for this CIN");

function isLeadSummaryPostedToFhir(row) {
    return row?.summary_posted_to_fhir === true;
}

/** Clinical score from list row when API provides it. */
function getLeadClinicalScore(row) {
    const raw =
        row?.lead_score ??
        row?.patient_clinical_score ??
        row?.clinical_score ??
        row?.patientClinicalScore ??
        "";
    return String(raw).trim();
}

/**
 * Eligibility status text + CSS modifier from list row.
 * Prefers `waystar_authorization`; falls back to common aliases.
 */
function getLeadEligibility(row) {
    const raw = String(
        row?.waystar_authorization ??
            row?.eligibility ??
            row?.eligibility_status ??
            ""
    ).trim();
    if (!raw) {
        return { label: "", tone: "unavailable" };
    }
    const lower = raw.toLowerCase();
    if (lower === "active") {
        return { label: "Active", tone: "active" };
    }
    if (lower === "rejected" || lower === "reject") {
        return { label: "Rejected", tone: "rejected" };
    }
    if (lower === "unavailable" || /^not\s+available$/i.test(raw)) {
        return { label: "Unavailable", tone: "unavailable" };
    }
    if (lower === "inactive") {
        return { label: "Inactive", tone: "unavailable" };
    }
    return { label: raw, tone: "unavailable" };
}

/** Enrollment % when an explicit enrollment field exists; otherwise blank. */
function getLeadEnrollmentDisplay(row) {
    const raw =
        row?.ecm_eligibility ??
        row?.enrollment ??
        row?.enrollment_pct ??
        row?.enrollment_likelihood ??
        row?.ecm_enrollment_likelihood ??
        "";
    if (raw == null || raw === "") {
        return "";
    }
    if (typeof raw === "number" && Number.isFinite(raw)) {
        return `${Math.round(raw)}%`;
    }
    const s = String(raw).trim();
    if (!s) {
        return "";
    }
    if (/%\s*$/.test(s)) {
        return s;
    }
    if (/^\d+(\.\d+)?$/.test(s)) {
        return `${s}%`;
    }
    return s;
}

function createEmptyFilters() {
    return {
        clinicalScore: [],
        eligibility: [],
        leadSource: []
    };
}

/** Fixed filter checkbox options — never derived from page/API row data. */
const LEADS_FILTER_OPTIONS = {
    clinicalScore: ["Low", "Moderate", "High"],
    eligibility: ["Active", "Rejected", "Unavailable", "Inactive"],
    leadSource: ["Field Intake","ClinNexus Outreach"]
};

const LEADS_LIST_RESTORE_KEY = "clinxos.leadsList.ui";

function normalizeFilterGroup(rawValues, allowedValues) {
    if (!Array.isArray(rawValues)) {
        return [];
    }
    const allowed = new Set(allowedValues);
    return rawValues
        .map((x) => String(x))
        .filter((value) => allowed.has(value));
}

function normalizeRestoreFilters(raw) {
    if (!raw || typeof raw !== "object") {
        return createEmptyFilters();
    }
    return {
        clinicalScore: normalizeFilterGroup(
            raw.clinicalScore,
            LEADS_FILTER_OPTIONS.clinicalScore
        ),
        eligibility: normalizeFilterGroup(
            raw.eligibility,
            LEADS_FILTER_OPTIONS.eligibility
        ),
        leadSource: normalizeFilterGroup(
            raw.leadSource,
            LEADS_FILTER_OPTIONS.leadSource
        )
    };
}

function writeLeadsListRestore(snapshot) {
    try {
        sessionStorage.setItem(LEADS_LIST_RESTORE_KEY, JSON.stringify(snapshot));
    } catch {
        // Ignore quota / private-mode failures.
    }
}

function clearLeadsListRestore() {
    try {
        sessionStorage.removeItem(LEADS_LIST_RESTORE_KEY);
    } catch {
        // Ignore private-mode failures.
    }
}

function countSelectedFilters(filters) {
    return Object.values(filters || {}).reduce(
        (sum, values) => sum + (Array.isArray(values) ? values.length : 0),
        0
    );
}

function toggleFilterValue(values, nextValue) {
    const list = Array.isArray(values) ? values : [];
    return list.includes(nextValue)
        ? list.filter((value) => value !== nextValue)
        : [...list, nextValue];
}

function SortIcon({ direction = null, active = false }) {
    return (
        <span
            className={active ? "leads-table-sort is-active" : "leads-table-sort"}
            aria-hidden="true"
        >
            <svg
                className="leads-table-sort__icon"
                width="12"
                height="18"
                viewBox="0 0 12 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M2.5 6.25L6 2.75L9.5 6.25"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    opacity={direction === "asc" ? "1" : direction ? "0.35" : "1"}
                />
                <path
                    d="M2.5 11.75L6 15.25L9.5 11.75"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    opacity={direction === "desc" ? "1" : direction ? "0.35" : "1"}
                />
            </svg>
        </span>
    );
}

SortIcon.propTypes = {
    direction: PropTypes.oneOf(["asc", "desc", null]),
    active: PropTypes.bool
};

function FilterIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path
                d="M4 5.5H20L14.5 12.5V18.5L9.5 20.5V12.5L4 5.5Z"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function EyeIcon() {
    return (
        <svg
            className="leads-table-view-icon"
            width="7.5"
            height="7.5"
            viewBox="0 0 9 9"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <path
                d="M8.25 8.25V0.75H0.75M8.25 0.75L0.75 8.25"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export class LeadsSummary extends React.Component {
    static propTypes = {
        query: PropTypes.object,
        settings: PropTypes.object,
        location: PropTypes.object,
        urlParams: PropTypes.object,
        router: PropTypes.object
    };

    constructor(props) {
        super(props);
        this._mounted = false;
        this._summaryTipTimer = null;
        this._summaryTipNode = null;
        this.filterMenuRef = React.createRef();
        this.filterButtonRef = React.createRef();
        this.state = {
            search: "",
            /** Active server-side search for POST /leads (`{ cin, first_name }`). */
            listQuery: { cin: "", first_name: "" },
            leads: [],
            /** 0-based page index; API uses 1-based `page`. */
            pageIndex: 0,
            pageSize: DEFAULT_LEADS_PAGE_SIZE,
            totalPages: 0,
            totalCount: 0,
            hasNext: false,
            hasPrev: false,
            listLoading: true,
            listPaging: false,
            listError: null,
            sortKey: null,
            sortDirection: null,
            filterMenuOpen: false,
            appliedFilters: createEmptyFilters(),
            draftFilters: createEmptyFilters(),
            filterSectionsOpen: {
                clinicalScore: true,
                eligibility: true,
                leadSource: true
            },
            /** Click-positioned “summary unavailable” tooltip after a blocked view click. */
            summaryTip: null,
            eligibilityInfoOpen: false
        };
    }

    componentDidMount() {
        this._mounted = true;
        const query = new URLSearchParams(this.props.location.search);
        const _tab = query.get("_tab");
        if (["demographics", "conditions", "tags"].includes(_tab)) {
            const clean = this.props.location.search || "";
            this.props.router.navigate(`/patient-list${clean}`);
            return;
        }
        if (!_tab || _tab !== "leadsSummary") {
            this.props.router.replace("/leads-summary?_tab=leadsSummary");
        }

        // Defer until after navigation updates so we do not setState on an unmounted instance (Strict Mode / hash replace).
        queueMicrotask(() => {
            if (this._mounted) {
                this.loadLeads();
            }
        });
    }

    componentWillUnmount() {
        this._mounted = false;
        if (this._summaryTipTimer) {
            clearTimeout(this._summaryTipTimer);
            this._summaryTipTimer = null;
        }
        document.removeEventListener("mousedown", this.handleDocumentMouseDown);
        document.removeEventListener("keydown", this.handleDocumentKeyDown);
        document.removeEventListener("keydown", this.handleEligibilityInfoKeyDown);
        document.body.style.overflow = "";
    }

    loadLeads = () => {
        if (!getLeadsListUrl()) {
            this.setState({
                listLoading: false,
                listError: "Leads list API is not configured (set VITE_LEAD_SERVICES_BASE, VITE_LEAD_SUMMARY_API_BASE, or VITE_LEADS_LIST_URL).",
                leads: [],
                pageIndex: 0,
                totalPages: 0,
                totalCount: 0,
                hasNext: false,
                hasPrev: false,
                listPaging: false
            });
            return;
        }

        const loc = this.props.location || this.props.router?.location;
        const fromNav =
            loc?.state?.leadsListRestore && typeof loc.state.leadsListRestore === "object"
                ? loc.state.leadsListRestore
                : null;

        // Only restore list UI when returning from member detail (router state).
        // Leaving for another app tab (e.g. Revenue Cycle) should start clean.
        if (!fromNav) {
            clearLeadsListRestore();
            this.fetchLeadsPage({ page: 1, loading: true });
            return;
        }

        const restore = {
            search: String(fromNav.search || ""),
            listQuery: {
                cin: String(fromNav.listQuery?.cin || ""),
                first_name: String(
                    fromNav.listQuery?.first_name || fromNav.listQuery?.firstName || ""
                )
            },
            pageIndex: Math.max(0, Number(fromNav.pageIndex) || 0),
            pageSize: LEADS_PAGE_SIZE_OPTIONS.includes(Number(fromNav.pageSize))
                ? Number(fromNav.pageSize)
                : DEFAULT_LEADS_PAGE_SIZE,
            sortKey: fromNav.sortKey || null,
            sortDirection:
                fromNav.sortDirection === "asc" || fromNav.sortDirection === "desc"
                    ? fromNav.sortDirection
                    : null,
            appliedFilters: normalizeRestoreFilters(fromNav.appliedFilters)
        };

        this.setState(
            {
                search: restore.search,
                listQuery: restore.listQuery,
                pageSize: restore.pageSize,
                sortKey: restore.sortKey,
                sortDirection: restore.sortDirection,
                appliedFilters: restore.appliedFilters,
                draftFilters: {
                    clinicalScore: [...restore.appliedFilters.clinicalScore],
                    eligibility: [...restore.appliedFilters.eligibility],
                    leadSource: [...restore.appliedFilters.leadSource]
                }
            },
            () => {
                this.fetchLeadsPage({
                    page: restore.pageIndex + 1,
                    loading: true
                });
            }
        );
    };

    getListRestoreSnapshot = () => {
        const {
            search,
            listQuery,
            pageIndex,
            pageSize,
            sortKey,
            sortDirection,
            appliedFilters
        } = this.state;
        return {
            search: String(search || ""),
            listQuery: {
                cin: String(listQuery?.cin || ""),
                first_name: String(listQuery?.first_name || "")
            },
            pageIndex: Math.max(0, Number(pageIndex) || 0),
            pageSize: LEADS_PAGE_SIZE_OPTIONS.includes(Number(pageSize))
                ? Number(pageSize)
                : DEFAULT_LEADS_PAGE_SIZE,
            sortKey: sortKey || null,
            sortDirection:
                sortDirection === "asc" || sortDirection === "desc" ? sortDirection : null,
            appliedFilters: normalizeRestoreFilters(appliedFilters)
        };
    };

    persistListRestore = () => {
        writeLeadsListRestore(this.getListRestoreSnapshot());
    };

    /**
     * POST /leads using current search, filters, sort, and page size.
     * @param {{ page?: number, loading?: boolean, paging?: boolean }} [opts]
     */
    fetchLeadsPage = ({ page = 1, loading = false, paging = false } = {}) => {
        if (!getLeadsListUrl()) {
            return;
        }
        const { listQuery, appliedFilters, sortKey, sortDirection, pageSize } = this.state;
        this.setState({
            listLoading: Boolean(loading),
            listPaging: Boolean(paging),
            listError: null
        });
        fetchLeadsList({
            page,
            pageSize,
            filters: appliedFilters,
            sortKey,
            sortDirection,
            search: listQuery
        })
            .then((result) => {
                if (!this._mounted) {
                    return;
                }
                this.applyLeadsPage(result);
            })
            .catch((err) => {
                if (!this._mounted) {
                    return;
                }
                this.setState({
                    listError: err.message || String(err),
                    listLoading: false,
                    listPaging: false,
                    ...(loading
                        ? {
                              leads: [],
                              pageIndex: 0,
                              totalPages: 0,
                              totalCount: 0,
                              hasNext: false,
                              hasPrev: false
                          }
                        : {})
                });
            });
    };

    /**
     * Run POST /leads from the current search box (Enter).
     * @param {string} raw
     */
    runListFetchFromSearch = (raw) => {
        if (!this._mounted || !getLeadsListUrl()) {
            return;
        }
        if (this.state.listLoading || this.state.listPaging) {
            return;
        }
        const listQuery = leadsListQueryFromSearchInput(raw);
        this.setState({ listQuery }, () => {
            this.fetchLeadsPage({ page: 1, loading: true });
        });
    };

    handleSearchChange = (e) => {
        const nextSearch = e.target.value;
        const hadActiveQuery = Boolean(
            String(this.state.listQuery?.cin || "").trim() ||
                String(this.state.listQuery?.first_name || "").trim()
        );
        const cleared = String(nextSearch || "").trim().length === 0;

        if (cleared && hadActiveQuery) {
            if (this.state.listLoading || this.state.listPaging) {
                this.setState({ search: nextSearch });
                return;
            }
            this.setState({ search: "", listQuery: { cin: "", first_name: "" } }, () => {
                this.fetchLeadsPage({ page: 1, loading: true });
            });
            return;
        }

        this.setState({ search: nextSearch });
    };

    handleClearSearch = () => {
        if (this.state.listLoading || this.state.listPaging) {
            return;
        }
        const hadActiveQuery = Boolean(
            String(this.state.listQuery?.cin || "").trim() ||
                String(this.state.listQuery?.first_name || "").trim()
        );
        this.setState({ search: "", listQuery: { cin: "", first_name: "" } }, () => {
            if (hadActiveQuery) {
                this.fetchLeadsPage({ page: 1, loading: true });
            }
        });
    };

    applyLeadsPage = (page) => {
        const pageNumber = Number(page?.page);
        const pageIndex = Number.isFinite(pageNumber) && pageNumber >= 1 ? pageNumber - 1 : 0;
        this.setState(
            {
                leads: Array.isArray(page?.items) ? page.items : [],
                pageIndex,
                totalPages: Number(page?.totalPages) || 0,
                totalCount: Number(page?.totalCount) || 0,
                hasNext: Boolean(page?.hasNext),
                hasPrev: Boolean(page?.hasPrev),
                listPaging: false,
                listLoading: false,
                listError: null
            },
            () => {
                this.persistListRestore();
            }
        );
    };

    handlePageSizeChange = (event) => {
        const nextSize = Number(event?.target?.value);
        if (
            !LEADS_PAGE_SIZE_OPTIONS.includes(nextSize) ||
            nextSize === this.state.pageSize ||
            this.state.listLoading ||
            this.state.listPaging
        ) {
            return;
        }
        this.setState({ pageSize: nextSize }, () => {
            this.fetchLeadsPage({ page: 1, paging: true });
        });
    };

    goPrevPage = () => {
        const { pageIndex, listLoading, listPaging, hasPrev } = this.state;
        if (!getLeadsListUrl() || listLoading || listPaging || !hasPrev || pageIndex <= 0) {
            return;
        }
        this.fetchLeadsPage({ page: pageIndex, paging: true });
    };

    goNextPage = () => {
        const { pageIndex, listLoading, listPaging, hasNext, totalPages } = this.state;
        const targetPage = pageIndex + 2;
        if (
            !getLeadsListUrl() ||
            listLoading ||
            listPaging ||
            !hasNext ||
            (totalPages > 0 && targetPage > totalPages)
        ) {
            return;
        }
        this.fetchLeadsPage({ page: targetPage, paging: true });
    };

    goToPage = (targetPageIndex) => {
        const { pageIndex, listLoading, listPaging, totalPages } = this.state;
        if (
            !getLeadsListUrl() ||
            listLoading ||
            listPaging ||
            targetPageIndex < 0 ||
            (totalPages > 0 && targetPageIndex >= totalPages) ||
            targetPageIndex === pageIndex
        ) {
            return;
        }
        this.fetchLeadsPage({ page: targetPageIndex + 1, paging: true });
    };

    getRows() {
        return Array.isArray(this.state.leads) ? this.state.leads : [];
    }

    openFilterMenu = () => {
        this.setState((prev) => ({
            filterMenuOpen: true,
            draftFilters: {
                clinicalScore: [...prev.appliedFilters.clinicalScore],
                eligibility: [...prev.appliedFilters.eligibility],
                leadSource: [...prev.appliedFilters.leadSource]
            },
            filterSectionsOpen: {
                clinicalScore: true,
                eligibility: true,
                leadSource: true
            }
        }));
        document.addEventListener("mousedown", this.handleDocumentMouseDown);
        document.addEventListener("keydown", this.handleDocumentKeyDown);
    };

    closeFilterMenu = () => {
        this.setState({ filterMenuOpen: false });
        document.removeEventListener("mousedown", this.handleDocumentMouseDown);
        document.removeEventListener("keydown", this.handleDocumentKeyDown);
    };

    handleDocumentMouseDown = (event) => {
        if (!this.state.filterMenuOpen) {
            return;
        }
        const menu = this.filterMenuRef.current;
        const button = this.filterButtonRef.current;
        const target = event.target;
        if (menu?.contains(target) || button?.contains(target)) {
            return;
        }
        this.closeFilterMenu();
    };

    handleDocumentKeyDown = (event) => {
        if (event.key === "Escape") {
            this.closeFilterMenu();
        }
    };

    handleFilterOptionToggle = (groupKey, optionValue) => {
        this.setState((prev) => ({
            draftFilters: {
                ...prev.draftFilters,
                [groupKey]: toggleFilterValue(prev.draftFilters[groupKey], optionValue)
            }
        }));
    };

    toggleFilterSection = (groupKey) => {
        this.setState((prev) => ({
            filterSectionsOpen: {
                ...prev.filterSectionsOpen,
                [groupKey]: !prev.filterSectionsOpen[groupKey]
            }
        }));
    };

    applyFilters = () => {
        if (this.state.listLoading || this.state.listPaging) {
            return;
        }
        this.setState(
            (prev) => ({
                appliedFilters: {
                    clinicalScore: [...prev.draftFilters.clinicalScore],
                    eligibility: [...prev.draftFilters.eligibility],
                    leadSource: [...prev.draftFilters.leadSource]
                },
                filterMenuOpen: false
            }),
            () => {
                document.removeEventListener("mousedown", this.handleDocumentMouseDown);
                document.removeEventListener("keydown", this.handleDocumentKeyDown);
                this.fetchLeadsPage({ page: 1, paging: true });
            }
        );
    };

    resetFilters = () => {
        if (this.state.listLoading || this.state.listPaging) {
            return;
        }
        const empty = createEmptyFilters();
        this.setState(
            {
                appliedFilters: empty,
                draftFilters: createEmptyFilters(),
                filterMenuOpen: false
            },
            () => {
                document.removeEventListener("mousedown", this.handleDocumentMouseDown);
                document.removeEventListener("keydown", this.handleDocumentKeyDown);
                this.fetchLeadsPage({ page: 1, paging: true });
            }
        );
    };

    toggleSort = (sortKey) => {
        if (this.state.listLoading || this.state.listPaging) {
            return;
        }
        this.setState(
            (prev) => {
                if (prev.sortKey !== sortKey) {
                    return {
                        sortKey,
                        sortDirection: "asc"
                    };
                }
                if (prev.sortDirection === "asc") {
                    return { sortKey, sortDirection: "desc" };
                }
                if (prev.sortDirection === "desc") {
                    return { sortKey: null, sortDirection: null };
                }
                return { sortKey, sortDirection: "asc" };
            },
            () => {
                // New sort overrides previous sort; keep filters/search and reset to page 1.
                this.fetchLeadsPage({ page: 1, paging: true });
            }
        );
    };

    renderSortableHeader(label, sortKey) {
        const active = this.state.sortKey === sortKey;
        const direction = active ? this.state.sortDirection : null;
        const nextDirection = active && this.state.sortDirection === "asc" ? "descending" : "ascending";
        return (
            <div className="leads-table-th-label">
                <button
                    type="button"
                    className={active ? "leads-table-sort-btn is-active" : "leads-table-sort-btn"}
                    onClick={() => this.toggleSort(sortKey)}
                    aria-label={`Sort ${label} ${nextDirection}`}
                >
                    <span className="leads-table-th-title">{label}</span>
                    <SortIcon direction={direction} active={active} />
                </button>
            </div>
        );
    }

    handleSearchKeyDown = (e) => {
        if (e.key !== "Enter") {
            return;
        }
        e.preventDefault();
        this.runListFetchFromSearch(e.currentTarget.value);
    };

    handleViewClick = (row, rowKey, event) => {
        event.stopPropagation();
        if (!isLeadSummaryPostedToFhir(row)) {
            this.showSummaryUnavailableTip(rowKey, event);
            return;
        }
        const cin = String(row?.cin || "").trim();
        if (!cin) {
            return;
        }
        this.persistListRestore();
        this.props.router.navigate(
            {
                pathname: `/leads-summary/member/${encodeURIComponent(cin)}`,
                search: "?_tab=leadsSummary"
            },
            {
                state: {
                    leadListRow: row,
                    leadsListRestore: this.getListRestoreSnapshot()
                }
            }
        );
    };

    showSummaryUnavailableTip = (rowKey, event) => {
        if (this._summaryTipTimer) {
            clearTimeout(this._summaryTipTimer);
        }
        const target = event?.currentTarget;
        const rect = target?.getBoundingClientRect?.();
        // Viewport coords + fixed portal so the tip never widens the table scroll area.
        const x = rect ? rect.left + rect.width / 2 : event?.clientX ?? 0;
        const y = rect ? rect.top : event?.clientY ?? 0;
        this.setState({ summaryTip: { rowKey, x, y } }, () => {
            this.clampSummaryTipPosition(this._summaryTipNode);
        });
        this._summaryTipTimer = setTimeout(() => {
            this._summaryTipTimer = null;
            if (this._mounted) {
                this.setState({ summaryTip: null });
            }
        }, 1500);
    };

    clampSummaryTipPosition = (el) => {
        if (!el || !this.state.summaryTip) {
            return;
        }
        const tipRect = el.getBoundingClientRect();
        const margin = 8;
        const overflowRight = tipRect.right - (window.innerWidth - margin);
        const overflowLeft = margin - tipRect.left;
        let { x } = this.state.summaryTip;
        if (overflowRight > 0) {
            x -= overflowRight;
        } else if (overflowLeft > 0) {
            x += overflowLeft;
        }
        if (x !== this.state.summaryTip.x) {
            this.setState({ summaryTip: { ...this.state.summaryTip, x } });
        }
    };

    setSummaryTipRef = (el) => {
        this._summaryTipNode = el;
        if (el) {
            this.clampSummaryTipPosition(el);
        }
    };

    renderSummaryUnavailableTip() {
        const { summaryTip } = this.state;
        if (!summaryTip || typeof document === "undefined") {
            return null;
        }
        return createPortal(
            <span
                ref={this.setSummaryTipRef}
                className="leads-member-action-tooltip"
                role="tooltip"
                style={{
                    left: summaryTip.x,
                    top: summaryTip.y
                }}
            >
                {SUMMARY_UNAVAILABLE_TOOLTIP}
            </span>,
            document.body
        );
    }

    renderPager(canGoPrev, canGoNext) {
        const { listPaging, pageIndex, totalPages, pageSize, totalCount } = this.state;
        const pageCount = Math.max(0, Number(totalPages) || 0);
        const visiblePages = getVisiblePageIndexes(pageIndex, pageCount);
        const rangeLabel = formatLeadsRecordRange(pageIndex, pageSize, totalCount);

        return (
            <div className="leads-summary-footer">
                <p className="leads-summary-footer__range">{rangeLabel}</p>

                <div className="leads-summary-paging" role="navigation" aria-label="Lead list pages">
                    <button
                        type="button"
                        className="leads-summary-paging__btn leads-summary-paging__btn--nav"
                        onClick={this.goPrevPage}
                        disabled={listPaging || !canGoPrev}
                        aria-label="Previous page"
                    >
                        <i className="fa fa-angle-left" aria-hidden="true" />
                        <span>Back</span>
                    </button>
                    <div className="leads-summary-paging__pages">
                        {visiblePages.map((pageNumber, idx) => {
                            if (pageNumber == null) {
                                return (
                                    <span
                                        key={`ellipsis-${idx}`}
                                        className="leads-summary-paging__ellipsis"
                                        aria-hidden="true"
                                    >
                                        …
                                    </span>
                                );
                            }
                            const active = pageNumber === pageIndex;
                            return (
                                <button
                                    key={pageNumber}
                                    type="button"
                                    className={
                                        active
                                            ? "leads-summary-paging__btn leads-summary-paging__btn--page is-active"
                                            : "leads-summary-paging__btn leads-summary-paging__btn--page"
                                    }
                                    onClick={() => this.goToPage(pageNumber)}
                                    disabled={listPaging}
                                    aria-current={active ? "page" : undefined}
                                    aria-label={`Go to page ${pageNumber + 1}`}
                                >
                                    {pageNumber + 1}
                                </button>
                            );
                        })}
                    </div>
                    <button
                        type="button"
                        className="leads-summary-paging__btn leads-summary-paging__btn--nav"
                        onClick={this.goNextPage}
                        disabled={listPaging || !canGoNext}
                        aria-label="Next page"
                    >
                        <span>Next</span>
                        <i className="fa fa-angle-right" aria-hidden="true" />
                    </button>
                </div>

                <div className="leads-summary-page-size">
                    <div className="leads-summary-page-size__row">
                        <label className="leads-summary-page-size__label" htmlFor="leads-page-size">
                            Result per page
                        </label>
                        <select
                            id="leads-page-size"
                            className="leads-summary-page-size__select"
                            value={pageSize}
                            onChange={this.handlePageSizeChange}
                            disabled={listPaging}
                            aria-label="Results per page"
                        >
                            {LEADS_PAGE_SIZE_OPTIONS.map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
        );
    }

    renderFilterSection(title, groupKey, options) {
        const selected = this.state.draftFilters[groupKey] || [];
        const isOpen = this.state.filterSectionsOpen[groupKey] !== false;
        if (!options.length) {
            return null;
        }
        return (
            <section className="leads-filter-menu__section" key={groupKey}>
                <button
                    type="button"
                    className="leads-filter-menu__section-head"
                    onClick={() => this.toggleFilterSection(groupKey)}
                    aria-expanded={isOpen}
                >
                    <h3 className="leads-filter-menu__section-title">{title}</h3>
                    <i
                        className={isOpen ? "fa fa-angle-up" : "fa fa-angle-down"}
                        aria-hidden="true"
                    />
                </button>
                {isOpen ? (
                    <div className="leads-filter-menu__options">
                        {options.map((option) => {
                            const checked = selected.includes(option);
                            const optionId = `filter-${groupKey}-${option.replace(/[^a-z0-9]+/gi, "-")}`;
                            return (
                                <label className="leads-filter-menu__option" key={option} htmlFor={optionId}>
                                    <input
                                        id={optionId}
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => this.handleFilterOptionToggle(groupKey, option)}
                                    />
                                    <span>{option}</span>
                                </label>
                            );
                        })}
                    </div>
                ) : null}
            </section>
        );
    }

    renderFilterMenu() {
        if (!this.state.filterMenuOpen) {
            return null;
        }
        const options = LEADS_FILTER_OPTIONS;
        const draftCount = countSelectedFilters(this.state.draftFilters);
        const appliedCount = countSelectedFilters(this.state.appliedFilters);
        const hasFilters = draftCount > 0 || appliedCount > 0;
        return (
            <div className="leads-filter-menu" ref={this.filterMenuRef}>
                <div className="leads-filter-menu__header">Filter by</div>
                <div className="leads-filter-menu__body">
                    {this.renderFilterSection("Patient Clinical Score", "clinicalScore", options.clinicalScore)}
                    {this.renderFilterSection("Eligibility", "eligibility", options.eligibility)}
                    {this.renderFilterSection("Outreach by", "leadSource", options.leadSource)}
                </div>
                <div className="leads-filter-menu__footer">
                    <button
                        type="button"
                        className={
                            hasFilters
                                ? "leads-filter-menu__reset is-active"
                                : "leads-filter-menu__reset"
                        }
                        onClick={this.resetFilters}
                        disabled={!hasFilters}
                    >
                        Reset
                    </button>
                    <button
                        type="button"
                        className={
                            draftCount > 0
                                ? "leads-filter-menu__apply is-active"
                                : "leads-filter-menu__apply"
                        }
                        onClick={this.applyFilters}
                        disabled={draftCount === 0}
                    >
                        {draftCount > 0 ? `Apply (${draftCount})` : "Apply"}
                    </button>
                </div>
            </div>
        );
    }

    renderTable(rows, { showPager = false, canGoPrev = false, canGoNext = false } = {}) {
        return (
            <>
                <div className="leads-table-wrap">
                    <div className="leads-table-scroll">
                        <table className="leads-table">
                            <thead>
                                <tr>
                                    <th scope="col" className="leads-table-col-cin">Member CIN</th>
                                    <th scope="col">Name</th>
                                    <th scope="col">Date Of Birth</th>
                                    <th scope="col">Lead Source</th>
                                    <th scope="col" className="leads-table-col-sort">
                                        {this.renderSortableHeader("Conv. Likelihood", "enrollment")}
                                    </th>
                                    <th scope="col">Patient Clinical Score</th>
                                    <th scope="col" className="leads-table-col-eligibility">
                                        Eligibility
                                    </th>
                                    <th scope="col">Outreach by</th>
                                    <th scope="col">Lead Status</th>
                                    <th scope="col" className="leads-table-col-view">
                                        View
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, index) => {
                                    const displayName = formatLeadFullName(row);
                                    const cin = String(row.cin || "").trim();
                                    const hasDobAge = Boolean(formatDobWithAge(row?.dob, row?.age));
                                    const leadSource = String(row.lead_source || "").trim();
                                    const leadSourcedBy = String(
                                        row.lead_sourced_by ??
                                            row.sourced_by ??
                                            row.leadSourcedBy ??
                                            ""
                                    ).trim();
                                    const leadStatus = getLeadStatusDisplay(row);
                                    const enrollment = getLeadEnrollmentDisplay(row);
                                    const clinicalScore = getLeadClinicalScore(row);
                                    const eligibility = getLeadEligibility(row);
                                    const summaryReady = isLeadSummaryPostedToFhir(row);
                                    const rowKey = leadRowKey(row, index);

                                    return (
                                        <tr key={rowKey}>
                                            <td className="leads-table-cell-cin">
                                                {cin || <span className="leads-table-na">-</span>}
                                            </td>
                                            <td className="leads-table-cell-name">{displayName}</td>
                                            <td className="leads-table-cell-dob">
                                                {hasDobAge ? (
                                                    <DobWithAgeDisplay
                                                        dob={row?.dob}
                                                        age={row?.age}
                                                        ageStyle="short"
                                                    />
                                                ) : (
                                                    <span className="leads-table-na">-</span>
                                                )}
                                            </td>
                                            <td>
                                                {leadSource ? (
                                                    leadSource
                                                ) : (
                                                    <span className="leads-table-na">-</span>
                                                )}
                                            </td>
                                            <td className="leads-table-cell-enrollment">
                                                {enrollment ? (
                                                    enrollment
                                                ) : (
                                                    <span className="leads-table-na">-</span>
                                                )}
                                            </td>
                                            <td className="leads-table-cell-score">
                                                {clinicalScore ? (
                                                    <LeadScoreBadge
                                                        score={clinicalScore}
                                                        displayCanonical
                                                    />
                                                ) : (
                                                    <span className="leads-table-na">-</span>
                                                )}
                                            </td>
                                            <td>
                                                {eligibility.label ? (
                                                    <span
                                                        className={`leads-eligibility leads-eligibility--${eligibility.tone}`}
                                                    >
                                                        {eligibility.label}
                                                    </span>
                                                ) : (
                                                    <span className="leads-table-na">-</span>
                                                )}
                                            </td>
                                            <td>
                                                {leadSourcedBy ? (
                                                    leadSourcedBy
                                                ) : (
                                                    <span className="leads-table-na">-</span>
                                                )}
                                            </td>
                                            <td>
                                                {leadStatus.label ? (
                                                    leadStatus.tone ? (
                                                        <span
                                                            className={`leads-lead-status leads-lead-status--${leadStatus.tone}`}
                                                        >
                                                            {leadStatus.label}
                                                        </span>
                                                    ) : (
                                                        leadStatus.label
                                                    )
                                                ) : (
                                                    <span className="leads-table-na">-</span>
                                                )}
                                            </td>
                                            <td className="leads-table-col-view">
                                                <div className="leads-table-view-slot">
                                                    <button
                                                        type="button"
                                                        className={
                                                            summaryReady
                                                                ? "leads-table-view-btn"
                                                                : "leads-table-view-btn leads-table-view-btn--disabled"
                                                        }
                                                        onClick={(e) =>
                                                            this.handleViewClick(row, rowKey, e)
                                                        }
                                                        aria-label={
                                                            summaryReady
                                                                ? `View summary for ${displayName}`
                                                                : `Summary unavailable for ${displayName}`
                                                        }
                                                        aria-disabled={
                                                            summaryReady ? undefined : true
                                                        }
                                                    >
                                                        <EyeIcon />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
                {showPager ? this.renderPager(canGoPrev, canGoNext) : null}
                {this.renderSummaryUnavailableTip()}
                {this.renderEligibilityInfoModal()}
            </>
        );
    }

    openEligibilityInfoModal = (event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (this.state.eligibilityInfoOpen) {
            return;
        }
        this.setState({ eligibilityInfoOpen: true });
        document.addEventListener("keydown", this.handleEligibilityInfoKeyDown);
        document.body.style.overflow = "hidden";
    };

    closeEligibilityInfoModal = () => {
        if (!this.state.eligibilityInfoOpen) {
            return;
        }
        this.setState({ eligibilityInfoOpen: false });
        document.removeEventListener("keydown", this.handleEligibilityInfoKeyDown);
        document.body.style.overflow = "";
    };

    handleEligibilityInfoKeyDown = (event) => {
        if (event.key === "Escape") {
            this.closeEligibilityInfoModal();
        }
    };

    renderEligibilityInfoModal() {
        if (!this.state.eligibilityInfoOpen) {
            return null;
        }

        const overlay = (
            <div
                className="lead-modal-overlay"
                role="presentation"
                onClick={this.closeEligibilityInfoModal}
            >
                <div
                    className="lead-modal lead-modal--popup lead-modal--eligibility"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="lead-eligibility-info-title"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="lead-modal__header">
                        <div className="lead-modal__title-row">
                            <h2 id="lead-eligibility-info-title" className="lead-modal__title">
                                Eligibility Status
                            </h2>
                        </div>
                        <button
                            type="button"
                            className="lead-modal__close"
                            onClick={this.closeEligibilityInfoModal}
                            aria-label="Close"
                        >
                            <i className="fa fa-times-circle" aria-hidden="true" />
                        </button>
                    </div>
                    <div className="lead-modal__body">
                        <ul className="lead-eligibility-info-list">
                            <li className="lead-eligibility-info-item">
                                <span className="leads-eligibility leads-eligibility--active">
                                    Active
                                </span>
                                <p className="lead-eligibility-info-item__desc">
                                    Eligibility is currently active and confirmed.
                                </p>
                            </li>
                            <li className="lead-eligibility-info-item">
                                <span className="leads-eligibility leads-eligibility--rejected">
                                    Rejected
                                </span>
                                <p className="lead-eligibility-info-item__desc">
                                    Member does not meet the eligibility criteria.
                                </p>
                            </li>
                            <li className="lead-eligibility-info-item lead-eligibility-info-item--with-bullets">
                                <span className="leads-eligibility leads-eligibility--unavailable">
                                    Unavailable
                                </span>
                                <p className="lead-eligibility-info-item__desc">
                                    Eligibility could not be confirmed at this time.
                                </p>
                                <ul className="lead-eligibility-info-item__bullets">
                                    <li>
                                        <strong>Data unavailable:</strong> Eligibility data is
                                        not available for this member.
                                    </li>
                                    <li>
                                        <strong>Error:</strong> Eligibility could not be
                                        retrieved due to a system error.
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        );

        if (typeof document === "undefined" || !document.body) {
            return overlay;
        }
        return createPortal(overlay, document.body);
    }

    renderEmptyTableState(message, { showPager = false, canGoPrev = false, canGoNext = false } = {}) {
        return (
            <>
                <div className="leads-table-wrap leads-table-wrap--empty">
                    <p className="leads-summary-empty leads-summary-empty--in-table">{message}</p>
                </div>
                {showPager ? this.renderPager(canGoPrev, canGoNext) : null}
            </>
        );
    }

    getEmptyListMessage(appliedFilterCount) {
        const cin = String(this.state.listQuery?.cin || "").trim();
        const firstName = String(this.state.listQuery?.first_name || "").trim();
        if (cin) {
            return "No CIN found.";
        }
        if (firstName) {
            return "No name found.";
        }
        if (appliedFilterCount > 0) {
            return "No leads match your selected filters.";
        }
        return "No leads to display.";
    }

    render() {
        const rows = this.getRows();
        const { listLoading, listPaging, listError, hasNext, hasPrev, totalPages, listQuery } =
            this.state;
        const searchBusy = listLoading || listPaging;
        const canGoPrev = Boolean(hasPrev);
        const canGoNext = Boolean(hasNext);
        const appliedFilterCount = countSelectedFilters(this.state.appliedFilters);
        const hasActiveSearch = Boolean(
            String(listQuery?.cin || "").trim() || String(listQuery?.first_name || "").trim()
        );
        const showPager =
            !listLoading &&
            !listError &&
            getLeadsListUrl() &&
            (canGoPrev ||
                canGoNext ||
                totalPages > 1 ||
                hasActiveSearch ||
                (Array.isArray(this.state.leads) && this.state.leads.length > 0));

        return (
            <div className="page">
                <div className="app-header">
                    <Header2
                        query={this.props.query}
                        settings={this.props.settings}
                        location={this.props.location}
                        urlParams={this.props.urlParams}
                    />
                </div>
                <div className="patient-search-results leads-list-shell leads-summary-list-page">
                    <div className="leads-summary-page leads-summary-page--list leads-summary-list-page">
                        <div className="leads-list-toolbar">
                            <div className="leads-list-toolbar__intro">
                                <h1 className="leads-summary-title">CARE MEMBERS</h1>
                            </div>
                            <div className="leads-list-toolbar__actions">
                                <div className="leads-list-search">
                                    <input
                                        type="text"
                                        className={
                                            String(this.state.search || "").length > 0
                                                ? "form-control leads-list-search__input has-clear"
                                                : "form-control leads-list-search__input"
                                        }
                                        placeholder="Search by Member CIN or First Name"
                                        value={this.state.search}
                                        onChange={this.handleSearchChange}
                                        onKeyDown={this.handleSearchKeyDown}
                                        disabled={searchBusy}
                                        aria-label="Search by Member CIN or First Name"
                                    />
                                    {String(this.state.search || "").length > 0 ? (
                                        <button
                                            type="button"
                                            className="leads-list-search__clear"
                                            onClick={this.handleClearSearch}
                                            disabled={searchBusy}
                                            aria-label="Clear search"
                                            title="Clear search"
                                        >
                                            <svg
                                                width="10"
                                                height="10"
                                                viewBox="0 0 10 10"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    d="M2 2l6 6M8 2L2 8"
                                                    stroke="currentColor"
                                                    strokeWidth="1.6"
                                                    strokeLinecap="round"
                                                />
                                            </svg>
                                        </button>
                                    ) : null}
                                </div>
                                <div className="leads-list-filter-wrap">
                                    <button
                                        type="button"
                                        className={
                                            appliedFilterCount > 0
                                                ? "leads-list-filter-btn is-active"
                                                : "leads-list-filter-btn"
                                        }
                                        aria-label="Filter leads"
                                        disabled={searchBusy}
                                        onClick={
                                            this.state.filterMenuOpen
                                                ? this.closeFilterMenu
                                                : this.openFilterMenu
                                        }
                                        ref={this.filterButtonRef}
                                    >
                                        <FilterIcon />
                                        {appliedFilterCount > 0 ? (
                                            <span className="leads-list-filter-badge">
                                                {appliedFilterCount}
                                            </span>
                                        ) : null}
                                    </button>
                                    {this.renderFilterMenu()}
                                </div>
                            </div>
                        </div>

                        <div className="leads-list-body">
                        {listLoading || listPaging
                            ? createPortal(
                                  <LeadLoadingSpinner
                                      page
                                      message="Loading care members..."
                                  />,
                                  document.body
                              )
                            : null}
                        {listLoading ? (
                            <div
                                className="leads-list-loading-placeholder"
                                aria-hidden="true"
                            />
                        ) : listError ? (
                            <div className="leads-summary-page-error">
                                <div className="alert alert-danger" role="alert">
                                    {listError}
                                </div>
                                <button type="button" className="btn btn-primary btn-sm" onClick={this.loadLeads}>
                                    Retry
                                </button>
                            </div>
                        ) : rows.length === 0 ? (
                            this.renderEmptyTableState(this.getEmptyListMessage(appliedFilterCount), {
                                showPager,
                                canGoPrev,
                                canGoNext
                            })
                        ) : (
                            this.renderTable(rows, { showPager, canGoPrev, canGoNext })
                        )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default connect((state) => state)(withRouter(LeadsSummary));
